import React from 'react';
import { useHistory } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid-pro';
import { GridRenderCellParams } from '@mui/x-data-grid/models/params/gridCellParams';
import Tooltip from '@mui/material/Tooltip';
import PauseCircleOutlinedIcon from '@mui/icons-material/PauseCircleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { BooleanSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/BooleanSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { TertiaryButton } from 'v3/Components/Shared/Buttons';
import { ReviewerStatusCell, SecondaryStatusPill } from 'v3/Components/Shared/StatusPill';
import { useEnums, useToasts } from 'Context';
import {
  buildEndpoint,
  defaultDataTransform,
  useApiWorker,
  useDataFetcher,
  useHasPermissionsV3,
  formatShortDateOnly,
} from 'Utilities';
import {
  FeatureFlagProvider,
  FeatureFlags,
} from 'Utilities/FeatureFlagProvider';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { ROUTES } from 'Navigation';
import { PermissionCodesV3 } from 'Models/Authorization/permissionCodesV3';
import ReviewersListHeader from './Reviewers.Header';
import './Reviewers.scss';

const formatShortDate = (value: string): string => formatShortDateOnly(value) || '-';

const dashIfEmpty = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '-';
  return String(value);
};

const toOptions = (enumObj: Record<string, any> | undefined) =>
  Object.values(enumObj ?? {})
    .map((e) => ({ value: String(e.value), label: e.displayName }))
    .filter((o): o is { value: string; label: string } => !!o.label);

const CONTACT_PREF_OPTIONS = [
  { value: '1', label: 'Call' },
  { value: '2', label: 'Text' },
  { value: '3', label: 'Email' },
];

// Only Name through Status are visible by default (STRAD2-2317); every other column starts hidden
// and can be turned on from the column picker.
const DEFAULT_VISIBLE_FIELDS = new Set([
  'name',
  'specialty',
  'subSpecialty',
  'license',
  'stateOfLicense',
  'cellPhoneNumber',
  'email',
  'status',
]);

type ArchiveReasonOption = { id: string; name: string };
const EMPTY_ARCHIVE_REASONS: ArchiveReasonOption[] = [];

const ReviewersV3Page: React.FC = () => {
  const history = useHistory();
  const hasPermission = useHasPermissionsV3();
  const {
    reviewerType,
    physicianSpeciality: specialtyEnum,
    licenseType: licenseTypeEnum,
    state: stateEnum,
  } = useEnums();

  const reviewerTypeOptions = React.useMemo(
    () => toOptions(reviewerType),
    [reviewerType]
  );
  const specialtyOptions = React.useMemo(
    () => toOptions(specialtyEnum),
    [specialtyEnum]
  );
  const licenseOptions = React.useMemo(
    () => toOptions(licenseTypeEnum),
    [licenseTypeEnum]
  );
  const stateOptions = React.useMemo(() => toOptions(stateEnum), [stateEnum]);

  const [archiveReasons] = useDataFetcher<ArchiveReasonOption[]>(
    buildEndpoint(ENDPOINTS.reviewers.base, 'archiveReasons'),
    EMPTY_ARCHIVE_REASONS,
    defaultDataTransform,
    undefined,
    true,
    '3.0'
  );
  const archiveReasonOptions = React.useMemo(
    () =>
      (archiveReasons ?? [])
        .map((r) => ({ value: r.id, label: r.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [archiveReasons]
  );

  const readEndpoint = buildEndpoint(
    ENDPOINTS.reviewers.endpointString('Read')
  );
  const exportEndpoint = buildEndpoint(
    ENDPOINTS.reviewers.endpointString('ExportToCsv')
  );
  const reportEndpoint = buildEndpoint(
    ENDPOINTS.reviewers.endpointString('GeneratePanelReport')
  );

  const legacyParams = React.useMemo(
    () => ({ readEndpoint, searchParams: {}, apiVersion: '3.0' }),
    [readEndpoint]
  );

  const toolbarPermissions = {
    showSearch: hasPermission(PermissionCodesV3.Reviewer.Panel.Search),
    showFilter: hasPermission(PermissionCodesV3.Reviewer.Panel.Filter),
    showExport: hasPermission(PermissionCodesV3.Reviewer.Panel.Export),
    showColumns: hasPermission(PermissionCodesV3.Reviewer.Panel.Columns),
  };

  const versionedAPI = useApiWorker(legacyParams.apiVersion);
  const toast = useToasts();
  const [isGeneratingReport, setIsGeneratingReport] = React.useState(false);

  const handleGenerateReport = React.useCallback(async () => {
    if (isGeneratingReport) return;
    setIsGeneratingReport(true);
    try {
      const response = await versionedAPI.post(reportEndpoint, null, {
        responseType: 'blob',
      });
      const contentDisposition: string =
        response.headers['content-disposition'] ?? '';
      const match = contentDisposition.match(
        /filename\*?=(?:UTF-8''|"?)([^";]+)"?/i
      );
      const fileName = match
        ? decodeURIComponent(match[1])
        : `${new Date().toISOString().slice(0, 10)} Physician Panel.xlsx`;
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // Surface the failure instead of leaving the user waiting on a file
      // that never downloads (STRAD2-2637).
      toast.error(
        (err as any)?.ErrorMessage ??
          'Error: the report could not be generated. No file was downloaded.'
      );
    } finally {
      setIsGeneratingReport(false);
    }
  }, [versionedAPI, reportEndpoint, isGeneratingReport, toast]);
  const columns: GridColDef[] = [
      {
        field: 'name',
        headerName: 'Name',
        width: 200,
        hideable: false,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
        renderCell: (params: GridRenderCellParams) => (
          <div className="reviewers-name-cell">
            <span className="reviewers-name-cell__label">
              {dashIfEmpty(params.value)}
            </span>
            {params.row.isOnHold && (
              <span className="reviewers-name-cell__hold-icon">
                <Tooltip title="On Hold">
                  <PauseCircleOutlinedIcon />
                </Tooltip>
              </span>
            )}
          </div>
        ),
      },
      {
        field: 'specialty',
        headerName: 'Specialty',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter {...params} optionItems={specialtyOptions} />
        ),
      },
      {
        field: 'subSpecialty',
        headerName: 'Subspecialty',
        width: 240,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'license',
        headerName: 'Lic.',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter {...params} optionItems={licenseOptions} />
        ),
      },
      {
        field: 'stateOfLicense',
        headerName: 'State Lic.',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter {...params} optionItems={stateOptions} />
        ),
      },
      {
        field: 'cellPhoneNumber',
        headerName: 'Cell Phone #',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'email',
        headerName: 'Email',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 180,
        hideable: true,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter
            {...params}
            optionItems={[
              { label: 'Active', value: '4' },
              { label: 'Archived', value: '5' },
              { label: 'Invited', value: '3' },
              { label: 'Not Ready', value: '2' },
              { label: 'Onboarding', value: '1' },
            ]}
          />
        ),
        renderCell: (params: GridRenderCellParams) =>
          ReviewerStatusCell({ ...params }),
      },

      {
        field: 'isProfileComplete',
        headerName: 'Is Complete',
        width: 160,
        hideable: true,
        valueFormatter: (value) =>
          value === true ? 'Yes' : value === false ? 'No' : '-',
        renderHeaderFilter: (params) => (
          <BooleanSelectFilter {...params} />
        ),
        renderCell: (params: GridRenderCellParams) =>
          params.value === true ? (
            <SecondaryStatusPill icon={<CheckCircleOutlineIcon />} label="Yes" />
          ) : (
            <SecondaryStatusPill icon={<ErrorOutlineOutlinedIcon />} label="No" />
          ),
      },
      {
        field: 'activePractice',
        headerName: 'Active Practice',
        width: 140,
        hideable: true,
        valueFormatter: (value) =>
          value === true ? 'Yes' : value === false ? 'No' : '-',
        renderHeaderFilter: (params) => (
          <BooleanSelectFilter {...params} />
        ),
      },
      {
        field: 'casesOut',
        headerName: 'Cases Out',
        width: 140,
        hideable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" />
        ),
      },
      {
        field: 'casesRejected',
        headerName: 'Cases Rejected',
        width: 140,
        hideable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" />
        ),
      },
      {
        field: 'lastCaseCompleted',
        headerName: 'Last Case Completed',
        width: 200,
        hideable: true,
        valueFormatter: (value: string) => formatShortDate(value),
        renderHeaderFilter: (params) => (
          <DateRangePickerFilterFilter {...params} />
        ),
      },
      {
        field: 'cosigner',
        headerName: 'Cosigner',
        width: 140,
        hideable: true,
        valueFormatter: (value) =>
          value === true ? 'Yes' : value === false ? 'No' : '-',
        renderHeaderFilter: (params) => (
          <BooleanSelectFilter {...params} />
        ),
      },
      {
        field: 'contactPreference',
        headerName: 'Contact Pref.',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <SingleSelectFilter
            {...params}
            optionItems={CONTACT_PREF_OPTIONS}
          />
        ),
      },
      {
        field: 'type',
        headerName: 'Type',
        width: 160,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <SingleSelectFilter
            {...params}
            optionItems={reviewerTypeOptions}
          />
        ),
      },
      {
        field: 'nextLicenseExpiration',
        headerName: 'Next Lic. Exp.',
        width: 200,
        hideable: true,
        valueFormatter: (value: string) => formatShortDate(value),
        renderHeaderFilter: (params) => (
          <DateRangePickerFilterFilter {...params} />
        ),
      },
      {
        // Must match the camelCased API property ("npi"), or the cell renders empty.
        field: 'npi',
        headerName: 'NPI #',
        width: 180,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'workPhone',
        headerName: 'Work Phone #',
        width: 180,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'activePracticeHoursPerWeek',
        headerName: 'Active Practice Hours Per Week',
        width: 260,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" />
        ),
      },
      {
        field: 'casesComplete',
        headerName: 'Cases Complete',
        width: 140,
        hideable: true,
        type: 'number',
        align: 'left',
        headerAlign: 'left',
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" />
        ),
      },
      {
        field: 'perCaseRate',
        headerName: 'Per Case Rate',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" allowDecimal />
        ),
      },
      {
        field: 'hourlyRate',
        headerName: 'Hourly Rate',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" allowDecimal />
        ),
      },
      {
        field: 'urpaRate',
        headerName: 'UR/PA Rate',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" allowDecimal />
        ),
      },
      {
        field: 'cosignRate',
        headerName: 'Cosign Rate',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <TextFieldFilter {...params} inputType="number" allowDecimal />
        ),
      },
      {
        field: 'followUpDate',
        headerName: 'Follow Up Date',
        width: 200,
        hideable: true,
        valueFormatter: (value: string) => formatShortDate(value),
        renderHeaderFilter: (params) => (
          <DateRangePickerFilterFilter {...params} />
        ),
      },
      {
        field: 'archiveReason',
        headerName: 'Archive Reason',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter
            {...params}
            optionItems={archiveReasonOptions}
          />
        ),
      },
      {
        field: 'reviewerAddressLine1',
        headerName: 'Reviewer Address Line 1',
        width: 400,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'reviewerAddressLine2',
        headerName: 'Reviewer Address Line 2',
        width: 400,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'reviewerCity',
        headerName: 'Reviewer City',
        width: 260,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'reviewerState',
        headerName: 'Reviewer State',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => (
          <MultiSelectFilter {...params} optionItems={stateOptions} />
        ),
      },
      {
        field: 'reviewerZip',
        headerName: 'Reviewer Zip',
        width: 140,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'contactPerson',
        headerName: 'Contact Person',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'contactEmail',
        headerName: 'Contact Email',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
      {
        field: 'contactPhone',
        headerName: 'Contact Phone #',
        width: 200,
        hideable: true,
        valueFormatter: dashIfEmpty,
        renderHeaderFilter: (params) => <TextFieldFilter {...params} />,
      },
  ];

  // Everything outside the default 8 starts hidden.
  const defaultColumnVisibility = columns
    .filter((c) => !DEFAULT_VISIBLE_FIELDS.has(c.field))
    .reduce<Record<string, boolean>>((acc, c) => {
      acc[c.field] = false;
      return acc;
    }, {});

  return (
    <div>
      <ReviewersListHeader />
      <div className="reviewers-content">
        <DataGrid
          title="Reviewers"
          gridId="reviewersList"
          enableUrlSync
          legacyParams={legacyParams}
          exportEndpoint={exportEndpoint}
          exportFileName="Reviewers"
          headerActions={
            <FeatureFlagProvider flag={FeatureFlags.V3ReviewerPanelReport}>
              {hasPermission(PermissionCodesV3.Reviewer.Panel.Report) && (
                <TertiaryButton
                  title="Generate Panel Report"
                  onClick={handleGenerateReport}
                  disabled={isGeneratingReport}
                />
              )}
            </FeatureFlagProvider>
          }
          toolbarPermissions={toolbarPermissions}
          searchVisibleColumnsOnly
          initialState={{
            pinnedColumns: { left: ['name'] },
            sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
          }}
          onRowClick={(params) =>
            history.push(`${ROUTES.REVIEWER_PROFILE}/${params.row.id}`)
          }
          columnVisibilityModel={defaultColumnVisibility}
          columns={columns}
        />
      </div>
    </div>
  );
};

export default ReviewersV3Page;
